import { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { ViewSwitcher } from "@/components/ViewSwitcher";
import { Pagination } from "@/components/Pagination";
import api, { formatApiErrorDetail } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Target, GraduationCap, User, Loader2, Search, Upload, Image as ImageIcon, X } from "lucide-react";
import TagPicker from "@/components/TagPicker";

const NONE = "__none__";
const ALL = "__all__";
const PAGE_SIZE = 20;

function ageFrom(birth) {
  if (!birth) return null;
  const b = new Date(birth);
  if (isNaN(b)) return null;
  return Math.floor((Date.now() - b.getTime()) / (365.25 * 24 * 3600 * 1000));
}

const GENDER_NONE = "__g__";
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/heic,.jpg,.jpeg,.png,.webp,.gif,.heic";
const emptyForm = {
  full_name: "", birth_date: "", diagnosis: "", photo_url: "", photo_path: "",
  gender: GENDER_NONE, services: [], strengths: [], support_areas: [],
  parent_id: NONE, teacher_id: NONE, notes: "",
};

function BepBadge({ n }) {
  return n > 0 ? (
    <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
      <Target className="mr-1 h-3 w-3" /> Aktif BEP ({n})
    </Badge>
  ) : (
    <Badge className="rounded-full bg-slate-100 text-slate-600 hover:bg-slate-100">
      <Target className="mr-1 h-3 w-3" /> BEP Planı Yok
    </Badge>
  );
}

function Avatar({ photo, name, size = "h-16 w-16", rounded = "rounded-2xl", text = "text-xl" }) {
  const src = mediaUrl(photo);
  return src ? (
    <img src={src} alt={name} className={`${size} flex-shrink-0 ${rounded} object-cover`} />
  ) : (
    <div className={`${size} flex flex-shrink-0 items-center justify-center ${rounded} bg-gradient-to-br from-violet-500 to-blue-600 ${text} font-bold text-white`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [pools, setPools] = useState({ diagnosis: [], services: [], strengths: [], support_areas: [] });
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("card");
  const [search, setSearch] = useState("");
  const [teacherFilter, setTeacherFilter] = useState(ALL);
  const [bepFilter, setBepFilter] = useState(ALL);
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef(null);

  const load = async () => {
    try {
      const [s, t, p, pl] = await Promise.all([
        api.get("/students"), api.get("/teachers"), api.get("/users?role=parent"), api.get("/pools"),
      ]);
      setStudents(s.data); setTeachers(t.data); setParents(p.data); setPools(pl.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const poolVals = (cat) => (pools[cat] || []).map((x) => x.value);
  const createOption = async (cat, val) => {
    try { await api.post("/pools", { category: cat, values: [val] }); const { data } = await api.get("/pools"); setPools(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { setPage(1); }, [search, teacherFilter, bepFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      const matchQ = !q || s.full_name.toLowerCase().includes(q) || (s.diagnosis || "").toLowerCase().includes(q);
      const matchTeacher = teacherFilter === ALL
        ? true
        : teacherFilter === NONE ? !s.teacher_id : s.teacher_id === teacherFilter;
      const matchBep = bepFilter === ALL
        ? true
        : bepFilter === "active" ? s.bep_active > 0 : s.bep_active === 0;
      return matchQ && matchTeacher && matchBep;
    });
  }, [students, search, teacherFilter, bepFilter]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      full_name: s.full_name || "", birth_date: s.birth_date || "", diagnosis: s.diagnosis || "",
      photo_url: s.photo_url || "", photo_path: s.photo_path || "", gender: s.gender || GENDER_NONE,
      services: s.services || [], strengths: s.strengths || [], support_areas: s.support_areas || [],
      parent_id: s.parent_id || NONE, teacher_id: s.teacher_id || NONE, notes: s.notes || "",
    });
    setOpen(true);
  };

  const uploadPhoto = async (file) => {
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, photo_url: data.url, photo_path: data.path }));
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Lütfen bir görsel dosyası seçin");
      return;
    }
    uploadPhoto(file);
  };

  const removePhoto = () => setForm((prev) => ({ ...prev, photo_url: "", photo_path: "" }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      gender: form.gender === GENDER_NONE ? "" : form.gender,
      services: form.services, strengths: form.strengths, support_areas: form.support_areas,
      parent_id: form.parent_id === NONE ? "" : form.parent_id,
      teacher_id: form.teacher_id === NONE ? "" : form.teacher_id,
    };
    try {
      if (editing) { await api.put(`/students/${editing.id}`, payload); toast.success("Öğrenci güncellendi"); }
      else { await api.post("/students", payload); toast.success("Öğrenci eklendi"); }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/students/${deleteTarget.id}`);
      toast.success("Öğrenci silindi"); setDeleteTarget(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="students-page">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Yönetim</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Öğrenciler</h1>
            <p className="mt-2 text-sm text-slate-600">Öğrenci ekle, düzenle ve veli/öğretmen ile eşleştir.</p>
          </div>
          <Button onClick={openCreate} data-testid="add-student-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700 active:scale-[0.98]">
            <Plus className="mr-1.5 h-4 w-4" /> Öğrenci Ekle
          </Button>
        </header>

        {/* Toolbar */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="İsim veya tanıya göre ara..." className="h-11 rounded-xl pl-10" data-testid="students-search" />
          </div>
          <Select value={teacherFilter} onValueChange={setTeacherFilter}>
            <SelectTrigger className="h-11 w-[190px] rounded-xl" data-testid="students-teacher-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm Öğretmenler</SelectItem>
              <SelectItem value={NONE}>Öğretmensiz</SelectItem>
              {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={bepFilter} onValueChange={setBepFilter}>
            <SelectTrigger className="h-11 w-[170px] rounded-xl" data-testid="students-bep-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tüm BEP Durumu</SelectItem>
              <SelectItem value="active">Aktif BEP</SelectItem>
              <SelectItem value="none">BEP Planı Yok</SelectItem>
            </SelectContent>
          </Select>
          <ViewSwitcher value={view} onChange={setView} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="students-empty">
            <GraduationCap className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            {students.length === 0 ? "Henüz öğrenci yok. “Öğrenci Ekle” ile başla." : "Aramanızla eşleşen öğrenci yok."}
          </Card>
        ) : view === "card" ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {paged.map((s) => {
              const age = ageFrom(s.birth_date);
              return (
                <Card key={s.id} data-testid={`student-card-${s.id}`}
                  className="group overflow-hidden rounded-2xl border-slate-200/60 p-0 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.03)] transition-transform hover:-translate-y-1">
                  <div className="flex items-start gap-4 border-t-4 border-violet-500 p-5">
                    <Avatar photo={s.photo_url} name={s.full_name} />
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-heading text-lg font-semibold text-slate-900">{s.full_name}</h3>
                      <p className="text-sm text-slate-500">{age != null ? `${age} yaş` : "Yaş —"}{s.diagnosis ? ` · ${s.diagnosis}` : ""}</p>
                      <div className="mt-2"><BepBadge n={s.bep_active} /></div>
                    </div>
                  </div>
                  <div className="space-y-1.5 border-t border-slate-100 px-5 py-4 text-sm">
                    <p className="flex items-center gap-2 text-slate-600"><GraduationCap className="h-4 w-4 text-violet-500" />Öğretmen: <span className="font-medium text-slate-800">{s.teacher_name || "Atanmadı"}</span></p>
                    <p className="flex items-center gap-2 text-slate-600"><User className="h-4 w-4 text-blue-500" />Veli: <span className="font-medium text-slate-800">{s.parent_name || "Atanmadı"}</span></p>
                  </div>
                  <div className="flex gap-2 border-t border-slate-100 px-5 py-3">
                    <Button variant="outline" size="sm" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Düzenle</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(s)} data-testid={`delete-student-${s.id}`} className="rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="mr-1 h-3.5 w-3.5" /> Sil</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="overflow-x-auto rounded-2xl border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="students-list">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Öğrenci</th>
                  <th className="px-5 py-3">Yaş</th>
                  <th className="px-5 py-3">BEP</th>
                  <th className="px-5 py-3">Öğretmen</th>
                  <th className="px-5 py-3">Veli</th>
                  <th className="px-5 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((s) => {
                  const age = ageFrom(s.birth_date);
                  return (
                    <tr key={s.id} data-testid={`student-row-${s.id}`} className="border-b border-slate-50 transition-colors last:border-0 hover:bg-slate-50/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar photo={s.photo_url} name={s.full_name} size="h-9 w-9" rounded="rounded-lg" text="text-sm" />
                          <div>
                            <p className="font-medium text-slate-800">{s.full_name}</p>
                            {s.diagnosis && <p className="text-xs text-slate-400">{s.diagnosis}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{age != null ? age : "—"}</td>
                      <td className="px-5 py-3"><BepBadge n={s.bep_active} /></td>
                      <td className="px-5 py-3 text-slate-600">{s.teacher_name || <span className="text-slate-400">Atanmadı</span>}</td>
                      <td className="px-5 py-3 text-slate-600">{s.parent_name || <span className="text-slate-400">Atanmadı</span>}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(s)} data-testid={`delete-student-${s.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        {!loading && <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="student-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Öğrenci Düzenle" : "Yeni Öğrenci"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2"><Label>Ad Soyad *</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="student-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Doğum Tarihi</Label><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="student-birth-input" /></div>
              <div className="space-y-2"><Label>Tanı / Durum</Label>
                <TagPicker value={form.diagnosis} onChange={(v) => setForm({ ...form, diagnosis: v })}
                  options={poolVals("diagnosis")} onCreate={(v) => createOption("diagnosis", v)}
                  placeholder="Tanı seç veya yaz" testid="student-diagnosis-picker" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cinsiyet</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="student-gender-select"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent><SelectItem value={GENDER_NONE}>Belirtilmedi</SelectItem><SelectItem value="Erkek">Erkek</SelectItem><SelectItem value="Kız">Kız</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fotoğraf</Label>
                <input ref={photoRef} type="file" accept={PHOTO_ACCEPT} capture="environment" onChange={onPickPhoto} className="hidden" data-testid="student-photo-input" />
                <div className="flex items-center gap-3">
                  {form.photo_url ? (
                    <img src={mediaUrl(form.photo_url)} alt="Önizleme" className="h-14 w-14 rounded-xl border border-slate-200 object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50">
                      <ImageIcon className="h-5 w-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} disabled={uploadingPhoto} className="rounded-lg">
                      {uploadingPhoto ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
                      {form.photo_url ? "Değiştir" : "Yükle"}
                    </Button>
                    {form.photo_url && (
                      <Button type="button" variant="ghost" size="sm" onClick={removePhoto} className="rounded-lg text-red-600 hover:bg-red-50">
                        <X className="mr-1 h-3.5 w-3.5" /> Kaldır
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400">JPG, PNG, WEBP · PC veya telefondan seçebilirsiniz</p>
              </div>
            </div>
            <div className="space-y-2"><Label>Aldığı Hizmetler</Label>
              <TagPicker multi value={form.services} onChange={(v) => setForm({ ...form, services: v })}
                options={poolVals("services")} onCreate={(v) => createOption("services", v)}
                placeholder="Hizmet seç veya ekle" testid="student-services-picker" /></div>
            <div className="space-y-2"><Label>Güçlü Yönleri</Label>
              <TagPicker multi value={form.strengths} onChange={(v) => setForm({ ...form, strengths: v })}
                options={poolVals("strengths")} onCreate={(v) => createOption("strengths", v)}
                placeholder="Güçlü yön seç veya ekle" testid="student-strengths-picker" /></div>
            <div className="space-y-2"><Label>Destek Gerektiren Alanlar</Label>
              <TagPicker multi value={form.support_areas} onChange={(v) => setForm({ ...form, support_areas: v })}
                options={poolVals("support_areas")} onCreate={(v) => createOption("support_areas", v)}
                placeholder="Alan seç veya ekle" testid="student-support-picker" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Veli</Label>
                <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                  <SelectTrigger data-testid="student-parent-select"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>Atanmadı</SelectItem>{parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Öğretmen</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger data-testid="student-teacher-select"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>Atanmadı</SelectItem>{teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.role === "institution_admin" ? " (Kurum Yön.)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Notlar</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="student-notes-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="student-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Öğrenciyi sil?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.full_name}</strong> kalıcı olarak silinecek. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-student" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
